/**
 * Budget Store - Zustand State Management
 *
 * Manages forecast budget (budget prévisionnel) state including list,
 * creation, update, deletion, duplication, and version history.
 */

import { create } from 'zustand';
import {
  Budget,
  BudgetSummary,
  BudgetVersion,
  BudgetFilters,
  BudgetLimitResponse,
  CreateBudgetRequest,
  UpdateBudgetRequest,
  DuplicateBudgetRequest,
  CreateBudgetFromStatementRequest,
} from '@budgira/shared';
import { budgetService } from '../services/budget.service';
import i18n from '../i18n';

interface BudgetState {
  // List state
  budgets: BudgetSummary[];
  totalCount: number;
  totalPages: number;
  isLoading: boolean;
  error: string | null;

  // Detail state
  currentBudget: Budget | null;
  isLoadingDetail: boolean;

  // Version history state
  versionHistory: BudgetVersion[];
  isLoadingVersions: boolean;

  // Limit state
  limit: BudgetLimitResponse | null;

  // Form state
  isSubmitting: boolean;

  // Filters and pagination
  filters: BudgetFilters;
  page: number;
  pageSize: number;

  // Actions
  fetchBudgets: () => Promise<void>;
  fetchBudget: (id: string) => Promise<void>;
  createBudget: (request: CreateBudgetRequest) => Promise<Budget | null>;
  createFromStatement: (request: CreateBudgetFromStatementRequest) => Promise<Budget | null>;
  updateBudget: (id: string, request: UpdateBudgetRequest) => Promise<Budget | null>;
  deleteBudget: (id: string) => Promise<boolean>;
  duplicateBudget: (id: string, request: DuplicateBudgetRequest) => Promise<Budget | null>;
  fetchVersionHistory: (budgetId: string) => Promise<void>;
  restoreVersion: (budgetId: string, version: number) => Promise<Budget | null>;
  checkLimit: () => Promise<void>;
  exportPdf: (id: string) => Promise<Blob | null>;
  setFilters: (filters: BudgetFilters) => void;
  setPage: (page: number) => void;
  clearCurrentBudget: () => void;
  clearVersionHistory: () => void;
  clearError: () => void;
  resetStore: () => void;
}

/**
 * Map backend error codes to localized messages
 */
function localizeBudgetError(err: unknown): string {
  let errorCode: string | undefined;
  let errorMessage: string | undefined;

  if (err && typeof err === 'object') {
    const axiosError = err as {
      response?: { data?: { error?: { code?: string; message?: string } } };
    };
    errorCode = axiosError.response?.data?.error?.code;
    errorMessage = axiosError.response?.data?.error?.message;
  }

  // Map error codes to translation keys
  const errorKeyMap: Record<string, string> = {
    BUDGET_NOT_FOUND: 'budgets.errors.notFound',
    STATEMENT_NOT_FOUND: 'budgets.errors.notFound',
    UNAUTHORIZED: 'budgets.errors.unauthorized',
    VALIDATION_ERROR: 'budgets.errors.validation',
    LIMIT_EXCEEDED: 'budgets.errors.limitExceeded',
    INVALID_DATE_RANGE: 'budgets.errors.invalidDateRange',
    NO_ACCOUNTS_SELECTED: 'budgets.errors.noAccountsSelected',
    ACCOUNT_NOT_FOUND: 'budgets.errors.accountNotFound',
    VERSION_NOT_FOUND: 'budgets.errors.versionNotFound',
  };

  if (errorCode && errorKeyMap[errorCode]) {
    const key = errorKeyMap[errorCode]!;
    return String(i18n.t(key));
  }

  if (err instanceof Error) {
    return err.message;
  }

  return errorMessage || String(i18n.t('budgets.errors.generic'));
}

export const useBudgetStore = create<BudgetState>((set, get) => ({
  budgets: [],
  totalCount: 0,
  totalPages: 0,
  isLoading: false,
  error: null,
  currentBudget: null,
  isLoadingDetail: false,
  versionHistory: [],
  isLoadingVersions: false,
  limit: null,
  isSubmitting: false,
  filters: {},
  page: 1,
  pageSize: 10,

  fetchBudgets: async (): Promise<void> => {
    set({ isLoading: true, error: null });
    try {
      const { filters, page, pageSize } = get();
      const result = await budgetService.list(filters, page, pageSize);
      set({
        budgets: result.items,
        totalCount: result.total,
        totalPages: result.totalPages,
        isLoading: false,
      });
    } catch (err) {
      const message = localizeBudgetError(err);
      set({ error: message, isLoading: false });
    }
  },

  fetchBudget: async (id: string): Promise<void> => {
    set({ isLoadingDetail: true, error: null });
    try {
      const budget = await budgetService.getById(id);
      set({ currentBudget: budget, isLoadingDetail: false });
    } catch (err) {
      const message = localizeBudgetError(err);
      set({ error: message, isLoadingDetail: false });
    }
  },

  createBudget: async (request: CreateBudgetRequest): Promise<Budget | null> => {
    set({ isSubmitting: true, error: null });
    try {
      const created = await budgetService.create(request);
      // Refresh the list
      await get().fetchBudgets();
      // Refresh limit
      await get().checkLimit();
      set({ isSubmitting: false });
      return created;
    } catch (err) {
      const message = localizeBudgetError(err);
      set({ error: message, isSubmitting: false });
      return null;
    }
  },

  createFromStatement: async (
    request: CreateBudgetFromStatementRequest
  ): Promise<Budget | null> => {
    set({ isSubmitting: true, error: null });
    try {
      const created = await budgetService.createFromStatement(request);
      // Refresh the list
      await get().fetchBudgets();
      // Refresh limit
      await get().checkLimit();
      set({ isSubmitting: false });
      return created;
    } catch (err) {
      const message = localizeBudgetError(err);
      set({ error: message, isSubmitting: false });
      return null;
    }
  },

  updateBudget: async (
    id: string,
    request: UpdateBudgetRequest
  ): Promise<Budget | null> => {
    set({ isSubmitting: true, error: null });
    try {
      const updated = await budgetService.update(id, request);
      // Refresh the list
      await get().fetchBudgets();
      // Update current budget if it's the one being edited
      if (get().currentBudget?.id === id) {
        set({ currentBudget: updated });
      }
      set({ isSubmitting: false });
      return updated;
    } catch (err) {
      const message = localizeBudgetError(err);
      set({ error: message, isSubmitting: false });
      return null;
    }
  },

  deleteBudget: async (id: string): Promise<boolean> => {
    set({ isSubmitting: true, error: null });
    try {
      await budgetService.delete(id);
      // Refresh the list
      await get().fetchBudgets();
      // Refresh limit
      await get().checkLimit();
      // Clear current budget if it's the one being deleted
      if (get().currentBudget?.id === id) {
        set({ currentBudget: null });
      }
      set({ isSubmitting: false });
      return true;
    } catch (err) {
      const message = localizeBudgetError(err);
      set({ error: message, isSubmitting: false });
      return false;
    }
  },

  duplicateBudget: async (
    id: string,
    request: DuplicateBudgetRequest
  ): Promise<Budget | null> => {
    set({ isSubmitting: true, error: null });
    try {
      const duplicated = await budgetService.duplicate(id, request);
      // Refresh the list
      await get().fetchBudgets();
      // Refresh limit
      await get().checkLimit();
      set({ isSubmitting: false });
      return duplicated;
    } catch (err) {
      const message = localizeBudgetError(err);
      set({ error: message, isSubmitting: false });
      return null;
    }
  },

  fetchVersionHistory: async (budgetId: string): Promise<void> => {
    set({ isLoadingVersions: true, error: null });
    try {
      const versions = await budgetService.getVersionHistory(budgetId);
      set({ versionHistory: versions, isLoadingVersions: false });
    } catch (err) {
      const message = localizeBudgetError(err);
      set({ error: message, isLoadingVersions: false });
    }
  },

  restoreVersion: async (budgetId: string, version: number): Promise<Budget | null> => {
    set({ isSubmitting: true, error: null });
    try {
      const restored = await budgetService.restoreVersion(budgetId, version);
      // Refresh version history
      await get().fetchVersionHistory(budgetId);
      // Update current budget
      set({ currentBudget: restored, isSubmitting: false });
      // Refresh the list
      await get().fetchBudgets();
      return restored;
    } catch (err) {
      const message = localizeBudgetError(err);
      set({ error: message, isSubmitting: false });
      return null;
    }
  },

  checkLimit: async (): Promise<void> => {
    try {
      const limit = await budgetService.checkLimit();
      set({ limit });
    } catch {
      // Silently fail - limit check is not critical
    }
  },

  exportPdf: async (id: string): Promise<Blob | null> => {
    try {
      return await budgetService.exportPdf(id);
    } catch (err) {
      const message = localizeBudgetError(err);
      set({ error: message });
      return null;
    }
  },

  setFilters: (filters: BudgetFilters): void => {
    set({ filters, page: 1 });
  },

  setPage: (page: number): void => {
    set({ page });
  },

  clearCurrentBudget: (): void => {
    set({ currentBudget: null });
  },

  clearVersionHistory: (): void => {
    set({ versionHistory: [] });
  },

  clearError: (): void => {
    set({ error: null });
  },

  resetStore: (): void => {
    set({
      budgets: [],
      totalCount: 0,
      totalPages: 0,
      isLoading: false,
      error: null,
      currentBudget: null,
      isLoadingDetail: false,
      versionHistory: [],
      isLoadingVersions: false,
      limit: null,
      isSubmitting: false,
      filters: {},
      page: 1,
      pageSize: 10,
    });
  },
}));

export default useBudgetStore;
